import { ReactElement, useCallback } from "react";
import {
	Icon,
	Spinner,
	TreeAccordion
} from "client/Components";
import { generateTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { IntegrationCredentialsLinkKind } from "client/Api/IntegrationCredentials/integrationCredentials";
import { showIncorrectWebDavCredentialsDialog } from "client_legacy/Documents/WebDav/Dialogs/incorrectCredentialsDialog";
import { ServerLoadingState } from "./WebDavStore";
import { useToggleExpansion } from "client/Components/TreeAccordion/useToggleExpansion";
import styles from "./WebDavSideBarMenu.module.less";
import {
	LinkKind,
	useLinkToIntegrationCredentialsDialog
} from "client/Features/Settings/IntegrationCredentials/LinkToIntegrationCredentialsDialog";
import { offerPasswordUpdateWhenPossibleChanged } from "client/Features/Settings/IntegrationCredentials/SyncPassword";
import { WebDavServerModel } from "client/Api/Documents/WebDav/webDav";

const WEB_DAV_ITEM_ID: string = "web-dav";

export type WebDavSideBarMenuProps = {
	serverLoadingState: ServerLoadingState;
	currentServerId?: number;
	onSelectServer(server: WebDavServerModel): void;
	onReloadSideBar(): void;
};

export const WebDavSideBarMenu = (props: WebDavSideBarMenuProps): ReactElement => {
	const {
		serverLoadingState,
		currentServerId,
		onSelectServer,
		onReloadSideBar
	} = props;

	const { isExpanded, toggleExpansion } = useToggleExpansion([WEB_DAV_ITEM_ID]);

	const [linkToIntegrationCredentialsDialog, openLinkToIntegrationCredentialsDialog] = useLinkToIntegrationCredentialsDialog();

	const clickHandler = useCallback(
		async (server?: WebDavServerModel): Promise<void> => {
			const { integrationCredentialsConfiguredLinks } = userConfiguration();

			if (!integrationCredentialsConfiguredLinks.fileServers) {
				const result: TryResult<void> = await openLinkToIntegrationCredentialsDialog({
					thingToLink: {
						kind: LinkKind.FileServers
					}
				});

				if (!result.wasSuccessful) {
					return;
				}

				onReloadSideBar();
				if (server) {
					onSelectServer(server);
				}
			} else {
				if (await offerPasswordUpdateWhenPossibleChanged({ kind: IntegrationCredentialsLinkKind.FileServers })) {
					onReloadSideBar();
				} else if (!server) {
					showIncorrectWebDavCredentialsDialog();
				}

				if (server) {
					onSelectServer(server);
				}
			}
		},
		[
			onReloadSideBar,
			onSelectServer,
			openLinkToIntegrationCredentialsDialog
		]
	);

	const toggleExpansionHandler = useCallback(
		(): void => toggleExpansion(WEB_DAV_ITEM_ID),
		[toggleExpansion]
	);

	return (
		<>
			<TreeAccordion trackingId="webdav-sidebar">
				<TreeAccordion.Node>
					<TreeAccordion.Item
						{...(
							serverLoadingState.kind === "success"
								? {
									expansionToggle: "visible",
									isExpanded: isExpanded(WEB_DAV_ITEM_ID),
									onToggleExpansion: toggleExpansionHandler
								}
								: {
									expansionToggle: "hidden"
								}
						)}
						trackingId="webdav-sidebar__header"
						icon="server"
						onClick={serverLoadingState.kind === "error"
							? clickHandler
							: toggleExpansionHandler
						}
					>
						<div className={styles.itemContent}>
							<div className={styles.itemContent__text}>{localize("_WebDavStoreTitle")}</div>
							{serverLoadingState.kind === "loading" && <Spinner isInline/>}
							{serverLoadingState.kind === "error"
								&& (
									<Icon
										value={{
											kind: "font",
											name: "triangle-exclamation",
											color: "danger"
										}}
									/>
								)
							}
						</div>
					</TreeAccordion.Item>
					{serverLoadingState.kind === "success" && (
						<TreeAccordion.SubTree
							isCollapsed={!isExpanded(WEB_DAV_ITEM_ID)}
						>
							{serverLoadingState.servers
								.map(
									(x: WebDavServerModel) => (
										<TreeAccordion.Node key={x.id}>
											<TreeAccordion.Item
												isSelected={currentServerId === x.id}
												onClick={() => clickHandler(x)}
												trackingId={generateTrackingId(WEB_DAV_ITEM_ID, `|server-${x.id}`)}
												indentionLevel={1}
												title={x.name}
												expansionToggle="hidden"
											>
												{x.name}
											</TreeAccordion.Item>
										</TreeAccordion.Node>
									)
								)
						}
						</TreeAccordion.SubTree>
					)}
				</TreeAccordion.Node>
			</TreeAccordion>
			{linkToIntegrationCredentialsDialog}
		</>
	);
};

WebDavSideBarMenu.displayName = "WebDavSideBarMenu";